import React from "react";
import styled from "styled-components";

import QuoteCard from "../../../components/quote-card/QuoteCard";
import type { ProfileReview } from "../../../types/profile";
import SectionTitle from "./SectionTitle";

export interface ProfileReviewsProps {
  reviews: ProfileReview[];
}

/**
 * レビューカードのグリッド。引用文を主役にするため、1 枚の最小幅は作品カード
 * より広い 360px を確保する。`auto-fill` なので狭い画面では自動的に 1 列に
 * なり、UA 判定（`isMobile`）は要らない。行間は影が潰れないよう広めに取る。
 */
const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 24px;
  width: 100%;
  /* QuoteCard が下方向へ落とすソフトな影のぶんの余白。 */
  padding-bottom: 8px;
`;

/**
 * 署名行に出す投稿者名。匿名（`status='2'`）や名前が解決できないレビューは
 * `undefined` を返し、`QuoteCard` に署名行を描画させない。
 */
const reviewerNameOf = (review: ProfileReview): string | undefined =>
  review.is_anonymous || !review.reviewer_name
    ? undefined
    : review.reviewer_name;

/**
 * レビューセクション。
 *
 * クリエイターがオファー履歴の中から「プロフィールに掲載する」と選んだ
 * レビュー（`show_on_profile`）のみを表示する。
 *
 * 表示は GALLERY のアーティストパネルと同じ共通コンポーネント `QuoteCard` を
 * 使い、映画レビューの引用のように「言葉」と「それを言った人」の重みを見せる。
 * 匿名（`status='2'`）のレビューは投稿者を明かせないため、署名行そのものを
 * 出さず引用文だけを見せる（`QuoteCard` は `name` が空なら署名行を描画しない）。
 *
 * 見出し・本文は言語に依らず共通のため、言語切替の対象になる文言は持たない。
 * セクション見出しは `SectionTitle`（共通）を使い、プロフィールテキスト等と
 * 同じデザイン（上に区切り線 + clamp フォント + 英大文字）に揃える。
 */
const ProfileReviews: React.FC<ProfileReviewsProps> = ({ reviews }) => {
  if (reviews.length === 0) return null;

  return (
    <section className="u-w100 u-col u-gap16 u-pt32 u-pb32">
      <SectionTitle>REVIEW</SectionTitle>
      <ReviewGrid>
        {reviews.map((review) => (
          <QuoteCard
            key={review.id}
            text={review.note}
            name={reviewerNameOf(review)}
          />
        ))}
      </ReviewGrid>
    </section>
  );
};

export default ProfileReviews;
